import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import Section from '../components/ui/Section';
import SectionHeader from '../components/ui/SectionHeader';

const WhyAFSC = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const differentiators = t('whyAFSC.differentiators', { returnObjects: true });

  return (
    <Section id="why-afsc" withFlyingElements={true}>
      {/* Mobile Layout - Centered with Parent Card */}
      <div className="md:hidden">
        <motion.div
          initial={{ opacity: 0, y: 30, filter: 'blur(10px)' }}
          whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-6xl mx-auto relative"
        >
          {/* Parent Card Content */}
          <div
            className={`relative px-6 py-8 ${
              isDark ? 'bg-afsc-slate/90' : 'bg-white/80'
            } backdrop-blur-sm rounded-2xl border ${
              isDark ? 'border-white/10' : 'border-afsc-charcoal/10'
            } transition-all duration-300 ${
              isDark ? 'hover:bg-afsc-slate' : 'hover:bg-white/90'
            } shadow-lg`}
          >
            {/* Centered Title */}
            <div className="text-center mb-12">
              <h2 className={`text-4xl font-heading font-bold mb-8 ${isDark ? 'text-white' : 'text-afsc-charcoal'}`}>
                {t('whyAFSC.title')} <span className="text-afsc-gold">{t('whyAFSC.titleHighlight')}</span>{t('whyAFSC.titleSuffix')}
              </h2>
            </div>

            {/* Description Cards */}
            <div className="mb-12">
              <div className="grid grid-cols-1 gap-4">
                {[t('whyAFSC.description1'), t('whyAFSC.description2')].map((description, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30, filter: 'blur(10px)' }}
                    whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.2, duration: 0.6 }}
                    className="relative group"
                  >
                    {/* Glowing background effect */}
                    <motion.div
                      className={`absolute -inset-0.5 rounded-lg blur-md opacity-60 group-hover:opacity-100 transition-opacity duration-300 ${
                        isDark ? 'bg-afsc-gold/40' : 'bg-afsc-gold/30'
                      }`}
                      animate={{
                        opacity: [0.4, 0.7, 0.4],
                      }}
                      transition={{
                        duration: 2 + index * 0.2,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }}
                    />
                    
                    {/* Card content */}
                    <div
                      className={`relative px-6 py-5 ${
                        isDark ? 'bg-afsc-slate/80' : 'bg-white/70'
                      } backdrop-blur-sm rounded-lg border ${
                        isDark ? 'border-afsc-gold/20' : 'border-afsc-gold/30'
                      } group-hover:border-afsc-gold/60 transition-all duration-300 ${
                        isDark ? 'group-hover:bg-afsc-slate/90' : 'group-hover:bg-white/80'
                      } shadow-lg ${
                        isDark ? 'shadow-afsc-gold/15' : 'shadow-afsc-gold/8'
                      } h-full`}
                      style={{
                        boxShadow: isDark
                          ? '0 0 15px rgba(203, 163, 90, 0.15), 0 0 30px rgba(203, 163, 90, 0.08)'
                          : '0 0 15px rgba(203, 163, 90, 0.12), 0 0 30px rgba(203, 163, 90, 0.06)',
                      }}
                    >
                      <p className={`text-base leading-relaxed ${isDark ? 'text-white/80' : 'text-afsc-charcoal/70'}`}>
                        {description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Cards Grid with Glowing Effect */}
            <div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {differentiators.map((item, index) => (
                  <motion.div
                    key={item}
                    initial={{ opacity: 0, scale: 0.8, filter: 'blur(10px)' }}
                    whileInView={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, duration: 0.6 }}
                    className="relative group"
                  >
                    {/* Glowing background effect */}
                    <motion.div
                      className={`absolute -inset-0.5 rounded-lg blur-md opacity-60 group-hover:opacity-100 transition-opacity duration-300 ${
                        isDark ? 'bg-afsc-gold/40' : 'bg-afsc-gold/30'
                      }`}
                      animate={{
                        opacity: [0.4, 0.7, 0.4],
                      }}
                      transition={{
                        duration: 2 + index * 0.2,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }}
                    />
                    
                    {/* Card content */}
                    <div
                      className={`relative px-4 py-3 ${
                        isDark ? 'bg-afsc-slate/80' : 'bg-white/70'
                      } backdrop-blur-sm rounded-lg border ${
                        isDark ? 'border-afsc-gold/20' : 'border-afsc-gold/30'
                      } group-hover:border-afsc-gold/60 transition-all duration-300 ${
                        isDark ? 'group-hover:bg-afsc-slate/90' : 'group-hover:bg-white/80'
                      } shadow-lg ${
                        isDark ? 'shadow-afsc-gold/15' : 'shadow-afsc-gold/8'
                      }`}
                      style={{
                        boxShadow: isDark
                          ? '0 0 15px rgba(203, 163, 90, 0.15), 0 0 30px rgba(203, 163, 90, 0.08)'
                          : '0 0 15px rgba(203, 163, 90, 0.12), 0 0 30px rgba(203, 163, 90, 0.06)',
                      }}
                    >
                      <span className={`font-medium text-sm ${isDark ? 'text-white' : 'text-afsc-charcoal'}`}>
                        {item}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Desktop Layout - Original 2-column layout */}
      <div className="hidden md:grid md:grid-cols-2 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, x: -50, filter: 'blur(10px)' }}
          whileInView={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className={`text-4xl md:text-5xl font-heading font-bold mb-6 ${isDark ? 'text-white' : 'text-afsc-charcoal'}`}>
            {t('whyAFSC.title')} <span className="text-afsc-gold">{t('whyAFSC.titleHighlight')}</span>{t('whyAFSC.titleSuffix')}
          </h2>
          <p className={`text-lg leading-relaxed mb-6 ${isDark ? 'text-white/80' : 'text-afsc-charcoal/70'}`}>
            {t('whyAFSC.description1')}
          </p>
          <p className={`text-lg leading-relaxed ${isDark ? 'text-white/80' : 'text-afsc-charcoal/70'}`}>
            {t('whyAFSC.description2')}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 50, filter: 'blur(10px)' }}
          whileInView={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="grid grid-cols-2 gap-4">
            {differentiators.map((item, index) => (
              <motion.div
                key={item}
                initial={{ opacity: 0, scale: 0.8, filter: 'blur(10px)' }}
                whileInView={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className={`px-4 py-3 md:px-6 md:py-4 ${isDark ? 'bg-afsc-slate/80' : 'bg-white/60'} backdrop-blur-sm rounded-lg border ${isDark ? 'border-white/10' : 'border-afsc-charcoal/10'} hover:border-afsc-gold/50 transition-all duration-300 ${isDark ? 'hover:bg-afsc-slate' : 'hover:bg-white/80'}`}
              >
                <span className={`font-medium text-sm ${isDark ? 'text-white' : 'text-afsc-charcoal'}`}>
                  {item}
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </Section>
  );
};

export default WhyAFSC;

